import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Printer, CheckCircle2, Clock } from "lucide-react";

export default function FixerInvoiceDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/fixer/invoices/${id}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [id]);

  if (!data) {
    return (
      <div className="tk-container py-20 text-slate-500" data-testid="invoice-loading">
        Laster …
      </div>
    );
  }

  const { invoice, line_items: items, fixer } = data;
  const isPaid = invoice.status === "paid";
  const created = new Date(invoice.created_at).toLocaleDateString("nb-NO");
  const paid = invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString("nb-NO") : null;

  return (
    <div className="tk-container py-12 max-w-3xl" data-testid="invoice-detail-page">
      <div className="flex justify-between items-center mb-6 print:hidden">
        <Link to="/verksted/faktura" className="text-sm text-slate-500 hover:text-[#0B1C30] inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Tilbake til fakturaer
        </Link>
        <button
          onClick={() => window.print()}
          className="tk-btn-primary"
          data-testid="print-pdf-btn"
        >
          <Printer size={14} /> Last ned som PDF
        </button>
      </div>

      <div className="tk-card !p-10 print:!shadow-none print:!border-0 print:!p-0" data-testid="invoice-receipt">
        {/* Header */}
        <div className="flex justify-between items-start mb-10 pb-6 border-b border-[#E2E8F0]">
          <div>
            <p className="font-bold text-xl tracking-tight text-[#0B1C30]">
              tekkdeler<span className="text-[#4A7C59]">.no</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">Tekkdeler AS · Org.nr 999 999 999</p>
            <p className="text-xs text-slate-500">post@tekkdeler.no</p>
          </div>
          <div className="text-right">
            <p className="tk-overline mb-1">Faktura</p>
            <p className="font-mono text-sm">#{invoice.id.slice(0, 8)}</p>
            <p className="text-xs text-slate-500 mt-1">Dato: {created}</p>
            {isPaid ? (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 mt-2">
                <CheckCircle2 size={12} /> Betalt {paid && `· ${paid}`}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-amber-50 text-amber-700 mt-2">
                <Clock size={12} /> Sendt — venter
              </span>
            )}
          </div>
        </div>

        {/* Parties */}
        <div className="grid grid-cols-2 gap-8 mb-10 text-sm">
          <div>
            <p className="tk-overline mb-2">Til</p>
            <p className="font-semibold">Tekkdeler AS</p>
            <p className="text-slate-600">Storgata 12, 0184 Oslo</p>
            <p className="text-slate-600">post@tekkdeler.no</p>
          </div>
          <div>
            <p className="tk-overline mb-2">Fra</p>
            <p className="font-semibold">{fixer.display_name}</p>
            <p className="text-slate-600">{fixer.address}, {fixer.postal_code}</p>
            {fixer.bank_account && (
              <p className="text-slate-600 font-mono text-xs">Kontonr: {fixer.bank_account}</p>
            )}
          </div>
        </div>

        {/* Line items */}
        <div className="mb-10">
          <p className="tk-overline mb-3">Oppdrag inkludert i fakturaen</p>
          <table className="w-full text-sm" data-testid="invoice-line-items">
            <thead>
              <tr className="text-left border-b border-[#E2E8F0]">
                <th className="py-2 pr-4 font-medium text-slate-500">Ordre</th>
                <th className="py-2 pr-4 font-medium text-slate-500">Produkt</th>
                <th className="py-2 pr-4 font-medium text-slate-500">Tjeneste</th>
                <th className="py-2 pr-4 font-medium text-slate-500 text-right">Beløp</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="4" className="py-4 text-slate-500 text-center">Ingen oppdrag på denne fakturaen.</td></tr>
              ) : (
                items.map((it) => (
                  <tr key={it.order_id} className="border-b border-[#E2E8F0]/70" data-testid={`line-${it.order_id}`}>
                    <td className="py-3 pr-4 font-mono text-xs">#{it.order_id.slice(0, 8)}</td>
                    <td className="py-3 pr-4">{it.product_name}</td>
                    <td className="py-3 pr-4 text-slate-600">{it.service_name}</td>
                    <td className="py-3 pr-4 text-right font-medium">kr {it.service_fee.toFixed(0)}</td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3" className="pt-4 text-right font-semibold">Total</td>
                <td className="pt-4 text-right font-semibold text-lg" data-testid="invoice-total">
                  kr {Number(invoice.amount).toFixed(0)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {invoice.notes && (
          <div className="mb-6 pt-6 border-t border-[#E2E8F0]">
            <p className="tk-overline mb-2">Notat fra verkstedet</p>
            <p className="text-sm italic text-slate-600">"{invoice.notes}"</p>
          </div>
        )}

        {isPaid && (
          <div
            className="bg-emerald-50 border border-emerald-200 rounded-md p-4 mb-2"
            data-testid="invoice-paid-confirmation"
          >
            <p className="text-sm text-emerald-800 flex items-center gap-2">
              <CheckCircle2 size={16} /> Faktura betalt {paid}. Bekreftelse er sendt til e-postadressen din.
            </p>
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400 text-center mt-6 print:hidden">
        Bruk knappen «Last ned som PDF» og velg «Lagre som PDF» i utskriftsvinduet.
      </p>
    </div>
  );
}
